/* DATA EXPLORER — the list a person lands on, and the Share dialog (2026-10-22).

   Max: "sẽ thêm tính năng share cho mọi người, nhiều người khác nhau hay group department, entity hay là private /
   rồi lúc vào sẽ hiện danh sách dashboard được tạo và được share bởi người khác". So the screen opens on the reports
   this person may open — theirs, and the ones shared with them — as Power BI's home does, and a report is opened from
   there. Whom a report reaches is a list of people, departments, entities or everybody; none is private. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TabIcon from './TabIcon';
import { Check, ChevronDown, Copy, FolderOpen, Globe2, Link2, Lock, Pencil, Plus, Search, Share2, Trash2, User, Users, Building2, X, AlertTriangle } from 'lucide-react';

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${p(d.getDate())}/${mon}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const KIND_ICON = { all: Globe2, team: Users, entity: Building2, user: User };

/* "Private", or the names it reaches — the first few, then how many more. */
export function ShareSummary({ t, shares, compact }) {
  if (!shares || !shares.length) {
    return <span className="dx-share-sum private"><Lock size={12} /> {t('xExplorer.privateOnly')}</span>;
  }
  const label = (s) => (s.kind === 'all' ? t('xExplorer.everyone') : s.label);
  const head = shares.slice(0, compact ? 2 : 3);
  const more = shares.length - head.length;
  return (
    <span className="dx-share-sum" title={shares.map(label).join(', ')}>
      {head.map((s) => {
        const Icon = KIND_ICON[s.kind] || User;
        return <span key={`${s.kind}|${s.key}`} className={`dx-share-tag k-${s.kind}`}><Icon size={11} /> {label(s)}</span>;
      })}
      {more > 0 && <span className="dx-muted">{t('xExplorer.sumMore', { n: more })}</span>}
    </span>
  );
}

/* The row's actions written out (2026-10-22, Max: "thay bằng cột action và có rõ các nút chứ ko để 3 chấm"): each a
   button with its word, coloured by what it does as Courier's are — Open blue, Share navy, Rename amber, Copy link
   violet, Delete red. Somebody reading a report shared with them gets Open and Copy link only. */
function RowActions({ t, v, onOpen, onRename, onShare, onLink, onDelete }) {
  const btn = (tone, Icon, label, fn) => (
    <button type="button" className={`dx-act tone-${tone}`} onClick={() => fn(v)} aria-label={`${label}: ${v.name}`} title={label}>
      <Icon size={13} /> <span>{label}</span>
    </button>
  );
  return (
    <span className="dx-acts">
      {btn('view', FolderOpen, t('xExplorer.open'), onOpen)}
      {v.mine && btn('submit', Share2, t('xExplorer.share'), onShare)}
      {(v.mine || v.canEdit) && btn('edit', Pencil, t('xExplorer.rename'), onRename)}
      {btn('copy', Copy, t('xExplorer.copyLink'), onLink)}
      {v.mine && btn('delete', Trash2, t('xExplorer.delete'), onDelete)}
    </span>
  );
}

export function ExplorerHome({ t, reports, builder, draft, dsName, onOpen, onNew, onContinue, onDiscard, onShare, onRename, onDelete, onLink }) {
  const mine = reports.filter((v) => v.mine);
  const shared = reports.filter((v) => !v.mine);
  const [tab, setTab] = useState(() => (builder && mine.length ? 'mine' : shared.length ? 'shared' : 'mine'));
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const base = tab === 'mine' ? mine : tab === 'shared' ? shared : reports;
    const needle = q.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((v) => [v.name, v.ownerName, dsName(v.dataset)].some((x) => String(x || '').toLowerCase().includes(needle)));
  }, [tab, mine, shared, reports, q, dsName]);
  const tabs = [
    ...(builder ? [['mine', t('xExplorer.homeMine'), mine.length]] : []),
    ['shared', t('xExplorer.homeShared'), shared.length],
    ...(builder ? [['all', t('xExplorer.homeAll'), reports.length]] : []),
  ];
  const empty = q ? t('xExplorer.homeEmptySearch') : tab === 'shared' ? t('xExplorer.homeEmptyShared') : t('xExplorer.homeEmptyMine');
  return (
    <div className="dx-home">
      {draft && (
        <div className="dx-draft" role="status">
          <span><b>{t('xExplorer.continueDraft')}</b> · {draft.name ? t('xExplorer.draftChanged', { name: draft.name }) : t('xExplorer.draftNew')}</span>
          <span className="dx-grow" />
          <button type="button" className="pa-btn" onClick={onDiscard}>{t('xExplorer.discardDraft')}</button>
          <button type="button" className="pa-btn primary" onClick={onContinue}>{t('xExplorer.continue')}</button>
        </div>
      )}
      <div className="dx-home-bar">
        <div className="pa-tabs dx-home-tabs view-tabs" role="tablist">
          {tabs.map(([k, label, n]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`pa-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>
              <TabIcon k={`dx-${k}`} />{label} <span className="pa-count">{n}</span>
            </button>
          ))}
        </div>
        <label className="dx-home-search">
          <Search size={14} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xExplorer.homeSearch')} aria-label={t('xExplorer.homeSearch')} />
        </label>
        <span className="dx-grow" />
        {builder && <button type="button" className="pa-btn primary" onClick={onNew}><Plus size={15} /> {t('xExplorer.newReport')}</button>}
      </div>
      <div className="pa-table-wrap dx-home-wrap">
        <table className="pa-table dx-home-table">
          <thead>
            <tr>
              <th>{t('xExplorer.colName')}</th>
              <th>{t('xExplorer.dataset')}</th>
              <th>{t('xExplorer.colOwner')}</th>
              <th>{t('xExplorer.colSharedWith')}</th>
              <th className="num">{t('xExplorer.pages')}</th>
              <th>{t('xExplorer.colUpdated')}</th>
              <th className="dx-home-act">{t('xExplorer.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.id} className="dx-home-row" onClick={() => onOpen(v)}>
                <td>
                  <button type="button" className="dx-home-name" onClick={(e) => { e.stopPropagation(); onOpen(v); }}>{v.name}</button>
                </td>
                <td>{dsName(v.dataset)}</td>
                <td>{v.mine ? <span className="dx-muted">{t('xExplorer.you')}</span> : v.ownerName}</td>
                <td>{v.mine ? <ShareSummary t={t} shares={v.shares} compact /> : <span className="dx-muted">{t('xExplorer.sharedWithYou')}{v.canEdit ? ` · ${t('xExplorer.roleEditor')}` : ''}</span>}</td>
                <td className="num">{v.spec?.pages?.length || 0}</td>
                <td className="dx-nowrap">{fmtWhen(v.updatedAt)}</td>
                <td className="dx-home-act" onClick={(e) => e.stopPropagation()}>
                  <RowActions t={t} v={v} onOpen={onOpen} onRename={onRename} onShare={onShare} onLink={onLink} onDelete={onDelete} />
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={7} className="dx-home-empty">{empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================================================================ Share */
/* Laid out as Google Drive's Share box (2026-10-24, Max: "tui muốn thiết kế cái share của data explore giống share của
   google drive á"): a box to add people, departments or entities; PEOPLE WITH ACCESS — the owner first, then each name
   with its role and a way to take it off; GENERAL ACCESS — Restricted or everybody who can see the data; Copy link and
   Done at the foot. The list it reaches is still saved WHOLE when Done is pressed, and who will actually see it is worked
   out as it is edited, from the server's own rule; anybody named who cannot open the dataset is said so — a share that
   quietly reaches nobody is the mistake this saves. A share is READ-ONLY (Viewer): the report is changed by its owner
   alone, so Viewer is the only role on offer. */
const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const AVATAR_TONES = ['#2563eb', '#0f766e', '#9333ea', '#c2410c', '#be123c', '#4d7c0f', '#0369a1', '#a16207'];
const toneOf = (key) => AVATAR_TONES[[...String(key)].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];

function Avatar({ kind, name, tone }) {
  if (kind === 'user') return <span className="dx-av" style={{ background: tone }} aria-hidden="true">{initials(name)}</span>;
  const Icon = KIND_ICON[kind] || User;
  return <span className={`dx-av icon k-${kind}`} aria-hidden="true"><Icon size={16} /></span>;
}

/* Drive's role menu: a button with the role and a caret, opening ROLE · Viewer · Editor (ticked), then the things that
   are not roles — Transfer ownership and Remove access. The list is FIXED to the button, not inside the row: the people
   list scrolls (and clips), and a menu cut off by it would hide the very choices it exists for. */
function RoleMenu({ t, id, role, open, onOpen, onClose, onRole, onTransfer, transferNote, onRemove, label }) {
  const btnRef = useRef(null);
  const [at, setAt] = useState(null);
  useEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const r = btnRef.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom > 260;
      setAt(below ? { top: r.bottom + 4, right: window.innerWidth - r.right } : { bottom: window.innerHeight - r.top + 4, right: window.innerWidth - r.right });
    };
    place();
    const away = (e) => { if (!e.target.closest?.(`[data-rolemenu="${id}"]`)) onClose(); };
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', onClose);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('resize', onClose); };
  }, [open, id, onClose]);
  return (
    <span className="dx-rolewrap" data-rolemenu={id}>
      <button type="button" ref={btnRef} className={`dx-share-role${open ? ' on' : ''}`} aria-haspopup="menu" aria-expanded={open}
        aria-label={label} onClick={() => (open ? onClose() : onOpen())}>
        {role === 'editor' ? t('xExplorer.roleEditor') : t('xExplorer.roleViewer')} <ChevronDown size={14} />
      </button>
      {open && at && (
        <div className="dx-rolemenu" role="menu" style={at}>
          <div className="dx-rolemenu-h">{t('xExplorer.roleHead')}</div>
          {['viewer', 'editor'].map((r) => (
            <button key={r} type="button" role="menuitemradio" aria-checked={role === r} className="dx-rolemenu-i"
              onClick={() => { onRole(r); onClose(); }}>
              <span className="dx-rolemenu-tick">{role === r ? <Check size={16} /> : null}</span>
              <span>{r === 'editor' ? t('xExplorer.roleEditor') : t('xExplorer.roleViewer')}</span>
            </button>
          ))}
          {(onTransfer || onRemove) && <div className="dx-rolemenu-sep" />}
          {onTransfer !== undefined && (
            <button type="button" role="menuitem" className="dx-rolemenu-i plain" disabled={!onTransfer} title={transferNote || undefined}
              onClick={() => { onClose(); onTransfer(); }}>
              <span>{t('xExplorer.transferOwnership')}</span>
            </button>
          )}
          {onRemove && (
            <button type="button" role="menuitem" className="dx-rolemenu-i plain" onClick={() => { onClose(); onRemove(); }}>
              <span>{t('xExplorer.removeAccess')}</span>
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export function ShareDialog({ t, view, call, dsLabel, onCancel, onSaved, onTransferred }) {
  const first = useMemo(() => (view.shares || []).map((s) => ({ kind: s.kind, key: s.key, role: s.role || 'viewer', label: s.label })), [view.shares]);
  const [targets, setTargets] = useState(first);
  const [lists, setLists] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState('');
  const [openList, setOpenList] = useState(false);
  const [menu, setMenu] = useState(null); // the row whose role menu is open
  const [handOver, setHandOver] = useState(null); // the person the owner is about to hand the report to
  const boxRef = useRef(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  useEffect(() => {
    let live = true;
    call(`/share-targets?dataset=${encodeURIComponent(view.dataset)}`)
      .then((r) => { if (live) setLists(r); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [call, view.dataset]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (menu) setMenu(null); else if (handOver) setHandOver(null); else if (openList) setOpenList(false); else onCancel(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openList, onCancel, menu, handOver]);
  useEffect(() => {
    if (!openList) return undefined;
    const down = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [openList]);

  const people = (n) => (n === 1 ? t('xExplorer.nPerson') : t('xExplorer.nPeople', { n }));
  const has = (kind, key) => targets.some((x) => x.kind === kind && String(x.key) === String(key));
  /* A pick closes the list (typing or a click in the box opens it again), so nothing stands over Done. */
  const add = (x) => { if (!has(x.kind, x.key)) setTargets((ts) => [...ts, { role: 'viewer', ...x }]); setQ(''); setOpenList(false); };
  const drop = (x) => setTargets((ts) => ts.filter((y) => !(y.kind === x.kind && String(y.key) === String(x.key))));
  const everyone = has('all', '');
  const setRole = (x, role) => setTargets((ts) => ts.map((y) => (y.kind === x.kind && String(y.key) === String(x.key) ? { ...y, role } : y)));
  const everyoneRole = targets.find((x) => x.kind === 'all')?.role || 'viewer';
  const setGeneral = (v) => setTargets((ts) => (v === 'all'
    ? (ts.some((x) => x.kind === 'all') ? ts : [...ts, { kind: 'all', key: '', role: 'viewer', label: '' }])
    : ts.filter((x) => x.kind !== 'all')));
  const named = targets.filter((x) => x.kind !== 'all');
  const sig = (list) => JSON.stringify(list.map(({ kind, key, role }) => `${kind}|${key}|${role || 'viewer'}`).sort());
  const dirty = sig(targets) !== sig(first);

  /* Everybody-at-once lives under General access, so the box offers the names only. */
  const options = useMemo(() => {
    if (!lists) return [];
    const needle = q.trim().toLowerCase();
    const hit = (...xs) => !needle || xs.some((x) => String(x || '').toLowerCase().includes(needle));
    const out = [];
    for (const x of lists.users) {
      if (has('user', String(x.id)) || !hit(x.name, x.email, x.login)) continue;
      out.push({ group: t('xExplorer.kind_user'), kind: 'user', key: String(x.id), label: x.name, sub: x.email || x.login, warn: !x.canOpen });
    }
    for (const x of lists.teams) if (!has('team', x.key) && hit(x.name, x.key)) out.push({ group: t('xExplorer.kind_team'), kind: 'team', key: x.key, label: x.name, sub: people(x.members) });
    for (const x of lists.entities) if (!has('entity', x.key) && hit(x.name, x.key)) out.push({ group: t('xExplorer.kind_entity'), kind: 'entity', key: x.key, label: x.name, sub: `${x.key} · ${people(x.members)}` });
    return out.slice(0, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists, q, targets, t]);

  /* Who it reaches, by the server's own rule, and who among them cannot open the data. */
  const reach = useMemo(() => {
    if (!lists) return null;
    const users = new Set(targets.filter((x) => x.kind === 'user').map((x) => String(x.key)));
    const teams = new Set(targets.filter((x) => x.kind === 'team').map((x) => x.key));
    const ents = new Set(targets.filter((x) => x.kind === 'entity').map((x) => x.key));
    const hitUsers = lists.users.filter((u) => (everyone && u.canOpen) || users.has(String(u.id))
      || u.teams.some((k) => teams.has(k)) || (u.entity && ents.has(u.entity)));
    return { n: hitUsers.filter((u) => u.canOpen).length, blocked: hitUsers.filter((u) => !u.canOpen) };
  }, [lists, targets, everyone]);

  /* What a row says under its name: the address, or what the department / entity holds. */
  const subOf = (x) => {
    if (!lists) return '';
    if (x.kind === 'user') { const u = lists.users.find((y) => String(y.id) === String(x.key)); return u ? (u.email || u.login) : ''; }
    if (x.kind === 'team') { const g = lists.teams.find((y) => y.key === x.key); return `${t('xExplorer.oneTeam')} · ${people(g ? g.members : 0)}`; }
    const g = lists.entities.find((y) => y.key === x.key);
    return `${t('xExplorer.oneEntity')} · ${x.key} · ${people(g ? g.members : 0)}`;
  };
  const blockedUser = (x) => x.kind === 'user' && lists?.users.find((u) => String(u.id) === String(x.key))?.canOpen === false;

  const done = async () => {
    if (!dirty) { onCancel(); return; }
    setBusy(true);
    setErr('');
    try {
      const r = await call(`/views/${view.id}/share`, { method: 'PUT', body: { targets: targets.map(({ kind, key, role }) => ({ kind, key, role })) } });
      onSaved(r.view);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  /* Handing over saves what the dialog holds first, so a change made a moment ago is not lost under the new owner. */
  const transfer = async () => {
    setBusy(true);
    setErr('');
    try {
      if (dirty) await call(`/views/${view.id}/share`, { method: 'PUT', body: { targets: targets.map(({ kind, key, role }) => ({ kind, key, role })) } });
      const r = await call(`/views/${view.id}/owner`, { method: 'POST', body: { userId: Number(handOver.key) } });
      onTransferred(r.owner);
    } catch (e) { setErr(e.message); setBusy(false); setHandOver(null); }
  };
  const transferBlock = (x) => {
    if (x.kind !== 'user') return null;
    const u = lists?.users.find((y) => String(y.id) === String(x.key));
    if (!u) return null;
    if (!u.canBuild) return t('xExplorer.transferNoBuild', { name: x.label });
    if (!u.canOpen) return t('xExplorer.transferNoData', { name: x.label, ds: dsLabel });
    return '';
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/data-explorer?report=${view.id}`); } catch { /* the address bar carries it */ }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const me = lists?.me;
  let lastGroup = null;
  return (
    <div className="dx-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dx-dialog dx-share" role="dialog" aria-modal="true" aria-label={t('xExplorer.shareTitle', { name: view.name })}>
        <div className="dx-dialog-h dx-share-h">
          <b>{t('xExplorer.shareTitle', { name: view.name })}</b>
          <button type="button" className="dx-vtool" onClick={onCancel} aria-label={t('xExplorer.fCancel')}><X size={16} /></button>
        </div>
        <div className="dx-share-pick" ref={boxRef}>
          <input type="text" className="dx-share-add" value={q} autoFocus disabled={!lists}
            onChange={(e) => { setQ(e.target.value); if (e.target.value) setOpenList(true); }} onFocus={() => setOpenList(true)}
            onClick={() => setOpenList(true)}
            placeholder={t('xExplorer.shareSearch')} aria-label={t('xExplorer.shareSearch')} />
          {openList && lists && (
            <div className="dx-share-list" role="listbox">
              {!options.length && <div className="dx-muted dx-menu-i">{t('xExplorer.noMatch')}</div>}
              {options.map((o) => {
                const head = o.group !== lastGroup ? <div className="dx-menu-h" key={`h-${o.group}`}>{o.group}</div> : null;
                lastGroup = o.group;
                return (
                  <React.Fragment key={`${o.kind}|${o.key}`}>
                    {head}
                    <button type="button" role="option" aria-selected={false} className="dx-menu-i dx-share-opt"
                      onClick={() => add({ kind: o.kind, key: o.key, label: o.label })}>
                      <Avatar kind={o.kind} name={o.label} tone={toneOf(`${o.kind}${o.key}`)} />
                      <span className="dx-share-who">
                        <span className="dx-share-optname">{o.label}</span>
                        <span className="dx-muted">{o.sub}</span>
                      </span>
                      {o.warn && <span className="dx-noaccess" title={t('xExplorer.noAccessHint', { ds: dsLabel })}>{t('xExplorer.noAccessTag')}</span>}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="dx-share-sec">{t('xExplorer.peopleWithAccess')}</div>
        <div className="dx-share-people">
          <div className="dx-share-row owner">
            <Avatar kind="user" name={me?.name || view.ownerName} tone={toneOf(`user${me?.id || ''}`)} />
            <span className="dx-share-who">
              <span className="dx-share-optname">{me ? `${me.name} ${t('xExplorer.youMark')}` : (view.ownerName || '')}</span>
              <span className="dx-muted">{me?.email || ''}</span>
            </span>
            <span className="dx-share-role fixed">{t('xExplorer.roleOwner')}</span>
          </div>
          {named.map((x) => {
            const blocked = blockedUser(x);
            return (
              <div key={`${x.kind}|${x.key}`} className={`dx-share-row${blocked ? ' blocked' : ''}`}>
                <Avatar kind={x.kind} name={x.label} tone={toneOf(`${x.kind}${x.key}`)} />
                <span className="dx-share-who">
                  <span className="dx-share-optname">{x.label}</span>
                  <span className="dx-muted">{subOf(x)}</span>
                </span>
                {blocked && <span className="dx-noaccess" title={t('xExplorer.noAccessHint', { ds: dsLabel })}><AlertTriangle size={11} /> {t('xExplorer.noAccessTag')}</span>}
                {(() => {
                  const id = `${x.kind}|${x.key}`;
                  const why = transferBlock(x);
                  return (
                    <RoleMenu t={t} id={id} role={x.role} open={menu === id} onOpen={() => setMenu(id)} onClose={closeMenu}
                      label={`${t('xExplorer.roleOf')}: ${x.label}`}
                      onRole={(r) => setRole(x, r)} onRemove={() => drop(x)}
                      onTransfer={x.kind !== 'user' ? undefined : (why ? null : () => setHandOver(x))} transferNote={why} />
                  );
                })()}
              </div>
            );
          })}
        </div>

        <div className="dx-share-sec">{t('xExplorer.generalAccess')}</div>
        <div className="dx-share-row general">
          <span className={`dx-av icon ${everyone ? 'k-all' : 'k-lock'}`} aria-hidden="true">{everyone ? <Globe2 size={16} /> : <Lock size={16} />}</span>
          <span className="dx-share-who">
            <select className="dx-share-gen" value={everyone ? 'all' : 'restricted'} onChange={(e) => setGeneral(e.target.value)}
              aria-label={t('xExplorer.generalAccess')}>
              <option value="restricted">{t('xExplorer.genRestricted')}</option>
              <option value="all">{t('xExplorer.genEveryone', { ds: dsLabel })}</option>
            </select>
            <span className="dx-muted">{everyone ? t('xExplorer.genEveryoneHint', { ds: dsLabel }) : t('xExplorer.genRestrictedHint')}</span>
          </span>
          {everyone && (
            <RoleMenu t={t} id="all|" role={everyoneRole} open={menu === 'all|'} onOpen={() => setMenu('all|')} onClose={closeMenu}
              label={`${t('xExplorer.roleOf')}: ${t('xExplorer.generalAccess')}`} onRole={(r) => setRole({ kind: 'all', key: '' }, r)} />
          )}
        </div>

        {reach && (
          <div className="dx-share-reach">
            {!targets.length ? t('xExplorer.sharePrivate')
              : reach.n === 1 ? t('xExplorer.shareReachOne') : t('xExplorer.shareReach', { n: reach.n })}
            {reach.blocked.length > 0 && (
              <div className="dx-share-warn">
                <AlertTriangle size={13} /> {t('xExplorer.shareCannot', { n: reach.blocked.length, ds: dsLabel })}
                <span className="dx-muted"> {reach.blocked.slice(0, 6).map((u) => u.name).join(', ')}{reach.blocked.length > 6 ? ` ${t('xExplorer.sumMore', { n: reach.blocked.length - 6 })}` : ''}</span>
              </div>
            )}
          </div>
        )}
        {handOver && (
          <div className="dx-share-handover" role="alertdialog" aria-label={t('xExplorer.transferOwnership')}>
            <b>{t('xExplorer.transferAsk', { name: handOver.label })}</b>
            <span className="dx-muted">{t('xExplorer.transferHint')}</span>
            <span className="dx-share-handover-b">
              <button type="button" className="pa-btn" onClick={() => setHandOver(null)} disabled={busy}>{t('xExplorer.fCancel')}</button>
              <button type="button" className="dx-share-done" onClick={transfer} disabled={busy}>{t('xExplorer.transferGo')}</button>
            </span>
          </div>
        )}
        {err && <div className="pa-banner err">{err}</div>}
        <div className="dx-dialog-f dx-share-f">
          <button type="button" className="dx-share-copy" onClick={copyLink}>
            {copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? t('xExplorer.linkCopied') : t('xExplorer.copyLink')}
          </button>
          <span className="dx-grow" />
          <button type="button" className="dx-share-done" onClick={done} disabled={busy || !lists}>{t('xExplorer.shareDone')}</button>
        </div>
      </div>
    </div>
  );
}
